import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Background,
  Connection,
  Controls,
  Edge,
  EdgeTypes,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import { Box, Button, useTheme } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { useTranslation } from 'react-i18next';
import { AgentDto } from '@shared/services/apiService/apiService';
import {
  buildHandoffCanvasGraph,
  HANDOFF_NODE_WIDTH,
  HandoffCanvasEdge,
  HandoffCanvasNode,
  HandoffRuleEditorValue,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import { AssistantNode } from './AssistantNode';
import { HandoffEdge } from './HandoffEdge';
import 'reactflow/dist/style.css';

type HandoffCanvasProps = {
  rules: HandoffRuleEditorValue[];
  agents: AgentDto[];
  nodePositions?: Record<string, { x: number; y: number }>;
  selectedRuleId: string | null;
  onSelectRule: (ruleId: string) => void;
  onConnectAgents: (sourceAgentId: string, targetAgentId: string) => void;
  onNodePositionChange?: (agentId: string, position: { x: number; y: number }) => void;
  onNodeSelect?: (agentId: string) => void;
  highlightedRuleId?: string | null;
  hasUnsavedChanges?: boolean;
  disabled?: boolean;
  height?: number | string;
};

const nodeTypes: NodeTypes = {
  assistantNode: AssistantNode,
};

const edgeTypes: EdgeTypes = {
  handoffEdge: HandoffEdge,
};

const FIT_VIEW_OPTIONS = {
  padding: 0.4,
  minZoom: 0.12,
  // Small squads need room to zoom in; keep maxZoom bounded so labels stay readable
  // without clipping MiniMap/Controls chrome.
  maxZoom: 1.35,
  duration: 0,
} as const;

const CANVAS_BOUNDS_PADDING = 2400;
const FIT_NODE_HEIGHT = 108;
const DEFAULT_RENDERED_NODE_HEIGHT = 64;

const getRenderedNodeDimensions = (node?: Pick<Node, 'width' | 'height'> | null) => ({
  width: node?.width && node.width > 0 ? node.width : HANDOFF_NODE_WIDTH,
  height: node?.height && node.height > 0 ? node.height : DEFAULT_RENDERED_NODE_HEIGHT,
});

const getNodesExtent = (flowNodes: Node[]): [[number, number], [number, number]] | undefined => {
  if (flowNodes.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  flowNodes.forEach((node) => {
    const width = Math.max(node.width ?? 0, HANDOFF_NODE_WIDTH);
    const height = Math.max(node.height ?? 0, FIT_NODE_HEIGHT);
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  });
  return [
    [minX - CANVAS_BOUNDS_PADDING, minY - CANVAS_BOUNDS_PADDING],
    [maxX + CANVAS_BOUNDS_PADDING, maxY + CANVAS_BOUNDS_PADDING],
  ];
};

const toReactFlowNodes = (nodes: HandoffCanvasNode[]): Node[] =>
  nodes.map((node) => ({
    ...node,
    type: 'assistantNode',
    width: HANDOFF_NODE_WIDTH,
  }));

const toReactFlowEdges = (
  edges: HandoffCanvasEdge[],
  selectedRuleId: string | null,
  highlightedRuleId: string | null,
  hasUnsavedChanges: boolean,
  isDarkMode: boolean,
): Edge[] =>
  edges.map((edge) => ({
    ...edge,
    type: 'handoffEdge',
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    data: edge.data,
    labelStyle: {
      fontSize: 12,
      fontWeight: 600,
      fill: isDarkMode ? '#d8e3f7' : '#344054',
    },
    labelBgStyle: {
      fill: isDarkMode ? '#0d1420' : '#FFFFFF',
      stroke: isDarkMode ? '#2a3c5f' : '#d0d5dd',
      strokeWidth: 1,
      opacity: 0.96,
    },
    labelBgPadding: [8, 4] as [number, number],
    labelBgBorderRadius: 8,
    animated: edge.data.ruleId === selectedRuleId,
    style: {
      strokeWidth:
        edge.data.ruleId === selectedRuleId
          ? 2.4
          : edge.data.ruleId === highlightedRuleId
            ? 2.1
            : 1.75,
      stroke:
        edge.data.ruleId === selectedRuleId
          ? isDarkMode
            ? '#63b3ff'
            : '#1976d2'
          : edge.data.ruleId === highlightedRuleId
            ? isDarkMode
              ? '#7cb6ff'
              : '#4f46e5'
            : isDarkMode
              ? '#64748b'
              : '#475467',
      strokeDasharray: hasUnsavedChanges ? '4 4' : undefined,
    },
  }));

export const HandoffCanvas: React.FC<HandoffCanvasProps> = ({
  rules,
  agents,
  nodePositions,
  selectedRuleId,
  onSelectRule,
  onConnectAgents,
  onNodePositionChange,
  onNodeSelect,
  highlightedRuleId = null,
  hasUnsavedChanges = false,
  disabled = false,
  height = 520,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);
  const isDraggingRef = useRef(false);
  const restoringViewRef = useRef(false);
  const [hasOffscreenAgents, setHasOffscreenAgents] = useState(false);
  const { t } = useTranslation();
  const graph = useMemo(
    () => buildHandoffCanvasGraph(rules, agents, nodePositions),
    [rules, agents, nodePositions],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(toReactFlowNodes(graph.nodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    toReactFlowEdges(graph.edges, selectedRuleId, highlightedRuleId, hasUnsavedChanges, isDarkMode),
  );
  const nodesExtent = useMemo(() => getNodesExtent(nodes), [nodes]);

  const fitGraphIntoView = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    if (!instance || graph.nodes.length === 0) return;
    // Double rAF waits until React Flow has measured node dimensions (chips
    // make assistant cards taller than the static placeholder size).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const measuredNodes = instance.getNodes().map((node) => ({
          ...node,
          width: Math.max(node.width ?? 0, HANDOFF_NODE_WIDTH),
          height: Math.max(node.height ?? 0, FIT_NODE_HEIGHT),
        }));
        restoringViewRef.current = true;
        try {
          instance.fitView({ ...FIT_VIEW_OPTIONS, nodes: measuredNodes });
        } finally {
          restoringViewRef.current = false;
        }
        setHasOffscreenAgents(false);
      });
    });
  }, [graph.nodes.length]);

  React.useEffect(() => {
    if (isDraggingRef.current) return;
    setNodes(toReactFlowNodes(graph.nodes));
  }, [graph.nodes, setNodes]);

  React.useEffect(() => {
    setEdges(
      toReactFlowEdges(
        graph.edges,
        selectedRuleId,
        highlightedRuleId,
        hasUnsavedChanges,
        isDarkMode,
      ),
    );
  }, [graph.edges, selectedRuleId, highlightedRuleId, hasUnsavedChanges, isDarkMode, setEdges]);

  React.useEffect(() => {
    if (graph.nodes.length === 0) return;
    fitGraphIntoView();
  }, [fitGraphIntoView, graph.nodes.length, graph.edges.length]);

  // After refresh, nodes land in state one tick later — fit again once they exist.
  React.useEffect(() => {
    if (isDraggingRef.current || nodes.length === 0) return;
    fitGraphIntoView();
  }, [fitGraphIntoView, nodes.length]);

  const clampNodePositionToViewport = useCallback(
    (nodeId: string, position: { x: number; y: number }): { x: number; y: number } => {
      const instance = reactFlowInstanceRef.current;
      const canvas = canvasRef.current;
      if (!instance || !canvas) return position;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return position;
      const { x: tx, y: ty, zoom } = instance.getViewport();
      if (!zoom || zoom <= 0) return position;
      const flowNode = instance.getNode(nodeId);
      const { width: nodeWidth, height: nodeHeight } = getRenderedNodeDimensions(flowNode);
      const margin = 16;
      const minX = (margin - tx) / zoom;
      const maxX = (rect.width - margin - nodeWidth * zoom - tx) / zoom;
      const minY = (margin - ty) / zoom;
      const maxY = (rect.height - margin - nodeHeight * zoom - ty) / zoom;
      return {
        x: minX <= maxX ? Math.min(Math.max(position.x, minX), maxX) : position.x,
        y: minY <= maxY ? Math.min(Math.max(position.y, minY), maxY) : position.y,
      };
    },
    [],
  );

  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      const clampedChanges = changes.map((change) => {
        if (change.type === 'position' && change.position) {
          return {
            ...change,
            position: clampNodePositionToViewport(change.id, change.position),
          };
        }
        return change;
      });
      onNodesChange(clampedChanges);
    },
    [clampNodePositionToViewport, onNodesChange],
  );

  const countOffscreenAgents = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    const canvas = canvasRef.current;
    if (!instance || !canvas) return { offscreen: 0, total: 0 };
    const flowNodes = instance.getNodes();
    const view = canvas.getBoundingClientRect();
    if (view.width <= 0 || view.height <= 0) return { offscreen: 0, total: flowNodes.length };
    const edgeMargin = 4;
    const offscreen = flowNodes.filter((node) => {
      const { width, height } = getRenderedNodeDimensions(node);
      const topLeft = instance.flowToScreenPosition(node.position);
      const bottomRight = instance.flowToScreenPosition({
        x: node.position.x + width,
        y: node.position.y + height,
      });
      return (
        topLeft.x < view.left + edgeMargin ||
        bottomRight.x > view.right - edgeMargin ||
        topLeft.y < view.top + edgeMargin ||
        bottomRight.y > view.bottom - edgeMargin
      );
    }).length;
    return { offscreen, total: flowNodes.length };
  }, []);

  const syncOffscreenAgents = useCallback(() => {
    setHasOffscreenAgents(countOffscreenAgents().offscreen > 0);
  }, [countOffscreenAgents]);

  const clampViewportToNodes = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    const canvas = canvasRef.current;
    if (!instance || !canvas || restoringViewRef.current) return false;
    const rect = canvas.getBoundingClientRect();
    const flowNodes = instance.getNodes();
    if (flowNodes.length === 0 || rect.width <= 0 || rect.height <= 0) return false;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    flowNodes.forEach((node) => {
      const { width, height } = getRenderedNodeDimensions(node);
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
      maxY = Math.max(maxY, node.position.y + height);
    });

    const margin = 16;
    const availW = Math.max(rect.width - margin * 2, HANDOFF_NODE_WIDTH);
    const availH = Math.max(rect.height - margin * 2, FIT_NODE_HEIGHT);
    const spanX = Math.max(maxX - minX, HANDOFF_NODE_WIDTH);
    const spanY = Math.max(maxY - minY, FIT_NODE_HEIGHT);
    const maxFittingZoom = Math.min(availW / spanX, availH / spanY, FIT_VIEW_OPTIONS.maxZoom);

    const viewport = instance.getViewport();
    const clampedZoom = Math.max(
      FIT_VIEW_OPTIONS.minZoom,
      Math.min(viewport.zoom, Math.max(FIT_VIEW_OPTIONS.minZoom, maxFittingZoom)),
    );

    const minTx = margin - minX * clampedZoom;
    const maxTx = rect.width - margin - maxX * clampedZoom;
    const minTy = margin - minY * clampedZoom;
    const maxTy = rect.height - margin - maxY * clampedZoom;

    const clampedX =
      minTx <= maxTx ? Math.min(Math.max(viewport.x, minTx), maxTx) : (minTx + maxTx) / 2;
    const clampedY =
      minTy <= maxTy ? Math.min(Math.max(viewport.y, minTy), maxTy) : (minTy + maxTy) / 2;

    if (
      Math.abs(clampedX - viewport.x) > 0.5 ||
      Math.abs(clampedY - viewport.y) > 0.5 ||
      Math.abs(clampedZoom - viewport.zoom) > 0.001
    ) {
      restoringViewRef.current = true;
      try {
        void instance.setViewport({ x: clampedX, y: clampedY, zoom: clampedZoom }, { duration: 0 });
      } finally {
        restoringViewRef.current = false;
      }
      return true;
    }
    return false;
  }, []);

  const fitAllAgents = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    if (!instance) return;
    restoringViewRef.current = true;
    const measuredNodes = instance.getNodes().map((node) => ({
      ...node,
      width: Math.max(node.width ?? 0, HANDOFF_NODE_WIDTH),
      height: Math.max(node.height ?? 0, FIT_NODE_HEIGHT),
    }));
    void instance.fitView({ ...FIT_VIEW_OPTIONS, nodes: measuredNodes });
    window.setTimeout(() => {
      restoringViewRef.current = false;
      syncOffscreenAgents();
    }, 80);
  }, [syncOffscreenAgents]);

  const restoreViewIfAgentsLeftScreen = useCallback(() => {
    if (isDraggingRef.current || restoringViewRef.current) return;
    clampViewportToNodes();
    const { offscreen, total } = countOffscreenAgents();
    if (total > 0 && offscreen > 0) {
      fitAllAgents();
      return;
    }
    syncOffscreenAgents();
  }, [clampViewportToNodes, countOffscreenAgents, fitAllAgents, syncOffscreenAgents]);

  const handleViewportMove = useCallback(() => {
    if (!isDraggingRef.current && !restoringViewRef.current) {
      clampViewportToNodes();
    }
    syncOffscreenAgents();
  }, [clampViewportToNodes, syncOffscreenAgents]);

  React.useEffect(() => {
    const frame = requestAnimationFrame(syncOffscreenAgents);
    return () => cancelAnimationFrame(frame);
  }, [nodes, syncOffscreenAgents]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let wheelTimer: number | null = null;
    const handleWheel = () => {
      syncOffscreenAgents();
      if (wheelTimer !== null) {
        window.clearTimeout(wheelTimer);
      }
      wheelTimer = window.setTimeout(() => {
        restoreViewIfAgentsLeftScreen();
      }, 160);
    };
    canvas.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      if (wheelTimer !== null) {
        window.clearTimeout(wheelTimer);
      }
    };
  }, [restoreViewIfAgentsLeftScreen, syncOffscreenAgents]);

  const onConnect = React.useCallback(
    (connection: Edge | Connection) => {
      if (disabled) return;
      if (connection.source && connection.target) {
        onConnectAgents(connection.source, connection.target);
      }
    },
    [disabled, onConnectAgents],
  );

  return (
    <Box
      ref={canvasRef}
      sx={{
        width: '100%',
        height,
        minHeight: 420,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        backgroundColor: (theme) => theme.palette.background.default,
        overflow: 'hidden',
        position: 'relative',
        isolation: 'isolate',
        zIndex: 0,
        // Keep React Flow layers clipped inside the canvas so nodes never paint
        // over the campaign sidebar or steal its clicks.
        '& .react-flow': {
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        },
        '& .react-flow__renderer, & .react-flow__pane': {
          overflow: 'hidden',
        },
        // Do not clip .react-flow__viewport / .react-flow__nodes at the initial
        // viewport rectangle — otherwise nodes dragged or placed outside (0..W, 0..H)
        // get clipped at the top/left (0, 0) boundary while SVG edges remain visible.
        '& .react-flow__viewport, & .react-flow__nodes, & .react-flow__edgelabel-renderer': {
          overflow: 'visible',
        },
        '& .react-flow__nodes, & .react-flow__node': {
          // HTML nodes must stay painted with the edge SVG. A promoted
          // viewport layer was dropping nodes while connection lines remained.
          overflow: 'visible',
          opacity: 1,
        },
        '& .react-flow__attribution': {
          backgroundColor: (theme) => theme.palette.background.paper,
          color: 'text.secondary',
          borderTopLeftRadius: 8,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '& .react-flow__controls': {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
          margin: 16,
        },
        '& .react-flow__controls-button': {
          backgroundColor: (theme) => theme.palette.background.paper,
          color: 'text.primary',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          width: 32,
          height: 32,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        },
      }}>
      <ReactFlow
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance;
          // Do not call zoomTo() after fitView — that undoes the fit and clips the graph.
          fitGraphIntoView();
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={(_, edge) => {
          if (disabled) return;
          const ruleId = (edge.data as { ruleId?: string } | undefined)?.ruleId;
          if (ruleId) onSelectRule(ruleId);
        }}
        defaultEdgeOptions={{ markerEnd: { type: 'arrowclosed' } }}
        onNodeClick={(_, node) => {
          if (disabled || !node?.id) return;
          // Node click opens agent details only; rules open from edges / Rules menu.
          onNodeSelect?.(node.id);
        }}
        onNodeDragStart={() => {
          isDraggingRef.current = true;
        }}
        onMove={handleViewportMove}
        onMoveEnd={restoreViewIfAgentsLeftScreen}
        onNodeDragStop={(_, node) => {
          isDraggingRef.current = false;
          if (!disabled && node?.id) {
            const clampedPosition = clampNodePositionToViewport(node.id, node.position);
            onNodePositionChange?.(node.id, clampedPosition);
          }
          restoreViewIfAgentsLeftScreen();
        }}
        nodesDraggable={!disabled}
        nodesConnectable={!disabled}
        elementsSelectable={!disabled}
        autoPanOnNodeDrag={false}
        translateExtent={nodesExtent}
        nodeExtent={nodesExtent}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        minZoom={FIT_VIEW_OPTIONS.minZoom}
        maxZoom={FIT_VIEW_OPTIONS.maxZoom}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}>
        <MiniMap
          pannable
          zoomable
          style={{
            backgroundColor: isDarkMode ? '#0b111d' : '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
            width: 140,
            height: 90,
            borderRadius: 8,
            margin: 16,
          }}
          nodeColor={isDarkMode ? '#334a6a' : '#c6daf4'}
          maskColor={isDarkMode ? 'rgba(7, 11, 20, 0.72)' : 'rgba(237, 243, 251, 0.78)'}
        />
        <Controls fitViewOptions={FIT_VIEW_OPTIONS} />
        <Background
          color={isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(0,0,0,0.06)'}
          gap={26}
          size={1}
        />
      </ReactFlow>
      {hasOffscreenAgents ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<CenterFocusStrongIcon fontSize="small" />}
          onClick={fitAllAgents}
          sx={(theme) => ({
            position: 'absolute',
            top: theme.spacing(2),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 6,
            boxShadow: theme.shadows[4],
          })}>
          {t('campaignPage.squads.editor.showAllAgents')}
        </Button>
      ) : null}
    </Box>
  );
};
